import type { ValuesToolbarProps } from './ValuesToolbar.tsx';
import {
    TextField,
    Grid,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogActions,
    Button,
    DialogContent,
} from '@mui/material';
import FieldNames from '../../../defaults/FieldNames.json';
import PostFireValues from '../../../defaults/PostFireValues.json';
import { useState, useEffect } from 'react';
import {useSelector} from "react-redux";

export interface ValuesDetailsPanelProps extends Partial<ValuesToolbarProps> {
    /** The value row being edited */
    valueRow?: any;
    /** The drawn geometry for new values */
    geometry?: any;
    /** The GlobalID of the parent feature */
    attributesGlobalID?: string | null | undefined;
    /** Called when save is complete */
    onSaveComplete?: (update: any) => void;
    /** Whether the modal is open */
    open?: boolean;
    /** Whether editing an existing value */
    edit?: boolean;
    /** Method to open the modal */
    openMethod?: () => void;
    /** Method to close the modal */
    handleCloseValues?: () => void;
    maxRank: number;
}

export default function ValuesDetailsPanel({
    open,
    handleCloseValues,
    attributesGlobalID,
    valueRow,
    geometry,
    onSaveComplete,
    maxRank,
}: ValuesDetailsPanelProps) {
    const [valueType, setValueType] = useState('');
    const [rank, setRank] = useState('');
    const [, setIsSaving] = useState(false);
    const isTreatment = useSelector((state) => state.details.isTreatment);


    // Initialize form fields from row data
    useEffect(() => {
        if (valueRow && valueRow[FieldNames.postFireValuesFields.globalID]) {
            // Editing existing value
            setValueType(valueRow[FieldNames.postFireValuesFields.valueType] || '');
            setRank(valueRow[FieldNames.postFireValuesFields.rank] || '');
        } else {
            // New value - clear fields
            setValueType('');
            setRank('');
        }
    }, [valueRow]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Update existing value feature
            const update: any = {
                attributes: {
                    ...(valueRow?.[FieldNames.postFireValuesFields.globalID]
                        ? {
                              [FieldNames.postFireValuesFields.globalID]:
                                  valueRow[FieldNames.postFireValuesFields.globalID],
                              [FieldNames.postFireValuesFields.objectId]:
                                  valueRow[FieldNames.postFireValuesFields.objectId],
                          }
                        : {}),
                    [FieldNames.postFireValuesFields.attributesGlobalID]: attributesGlobalID,
                    [FieldNames.postFireValuesFields.valueType]: valueType,
                    [FieldNames.postFireValuesFields.rank]: rank ? parseInt(rank, 10) : null,
                },
            };

            if (geometry) {
                update.attributes._geometry = geometry.toJSON ? geometry.toJSON() : geometry;
            }

            onSaveComplete?.(update);
        } catch (error) {
            console.error('Error saving value feature:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = parseInt(e.target.value, 10);

        // Clamp the value to your min/max limits
        if (val > maxRank) val = maxRank;
        if (val < 1) val = 1;

        // Handle empty input gracefully or fall back to your min boundary
        setRank(isNaN(val) ? '' : val.toString());
    };

    return (
        <Dialog open={open ?? false} onClose={onSaveComplete} maxWidth='md' fullWidth>
            <DialogTitle>Add Postfire Value</DialogTitle>
            <DialogContent>
                <Grid
                    container
                    direction='row'
                    size={12}
                    style={{ alignItems: 'center', paddingTop: 10 }}
                >
                    <Grid size={6}>
                        <Autocomplete
                            value={valueType || null}
                            options={
                                isTreatment ? PostFireValues.Treatment : PostFireValues.Activity
                            }
                            onChange={(_e, newValue) => setValueType(newValue || '')}
                            renderInput={(params) => <TextField {...params} label='Category' />}
                        ></Autocomplete>
                    </Grid>
                    <Grid size={6}>
                        <TextField
                            fullWidth
                            label='Rank'
                            value={rank}
                            onChange={handleRankChange}
                            variant='outlined'
                            type='number'
                            slotProps={{ htmlInput: { min: 1, max: maxRank } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseValues}>Cancel</Button>
                <Button onClick={handleSave} variant='contained' disabled={!rank || !valueType}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}
